import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { engagementsApi } from '../api/engagements.js';
import { eventsApi } from '../api/events.js';
import { speakersApi } from '../api/speakers.js';
import { StatusBadge } from '../components/common/StatusBadge.jsx';
import { LoadingSpinner } from '../components/common/LoadingSpinner.jsx';
import { ErrorAlert } from '../components/common/ErrorAlert.jsx';
import { useAuth } from '../hooks/useAuth.jsx';
import { usePageHeader } from '../hooks/usePageHeader.jsx';
import { formatDate, todayISO } from '../utils/dates.js';
import { ENGAGEMENT_STATUSES, SPEAKER_STATUSES } from '../utils/constants.js';

function StatCard({ value, label, to, variant }) {
  const content = (
    <div
      className="stat-card"
      style={variant ? {
        background: `var(--color-${variant}-bg)`,
        borderColor: `var(--color-${variant})`,
      } : undefined}
    >
      <div className="stat-value" style={variant ? { color: `var(--color-${variant})` } : undefined}>
        {value}
      </div>
      <div className="stat-label">{label}</div>
    </div>
  );
  return to ? <Link to={to} style={{ textDecoration: 'none' }}>{content}</Link> : content;
}

/**
 * A card of up to 5 clickable rows, each linking to one record's detail page.
 * `accent` is one of the --color-entity-* tokens, giving each dashboard card
 * its own quiet, consistent color identity (a thin top border) — reused
 * wherever this record type shows up elsewhere, rather than a one-off decoration.
 */
function ListCard({ title, viewAllTo, items, emptyText, renderItem, accent }) {
  return (
    <section className="card stack-2" style={accent ? { borderTop: `3px solid var(--color-entity-${accent})` } : undefined}>
      <div className="between">
        <h2 style={{ fontSize: 'var(--text-base)' }}>{title}</h2>
        {viewAllTo && <Link to={viewAllTo} style={{ fontSize: 'var(--text-xs)' }}>View all →</Link>}
      </div>
      {items.length === 0 ? (
        <p style={{ color: 'var(--color-text-muted)', fontSize: 'var(--text-sm)' }}>{emptyText}</p>
      ) : (
        <div>
          {items.map((item, i) => {
            const { primary, meta } = renderItem(item);
            return (
              <Link
                key={item.id}
                to={item.to}
                className="between"
                style={{
                  padding: 'var(--space-2) 0',
                  borderBottom: i < items.length - 1 ? '1px solid var(--color-border)' : 'none',
                  fontSize: 'var(--text-sm)',
                  textDecoration: 'none',
                  color: 'inherit',
                  gap: 'var(--space-3)',
                }}
              >
                <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {primary}
                </span>
                <span style={{ color: 'var(--color-text-muted)', fontSize: 'var(--text-xs)', flexShrink: 0, textAlign: 'right' }}>
                  {meta}
                </span>
              </Link>
            );
          })}
        </div>
      )}
    </section>
  );
}

/** Same card shell as ListCard, but for a status→count breakdown instead of
 * a list of records — kept as its own small component so it can sit in the
 * same 25%-width grid row as the two ListCards next to it. */
function StatusCard({ title, statuses, counts, type, hrefBase }) {
  return (
    <section className="card stack-2">
      <h2 style={{ fontSize: 'var(--text-base)' }}>{title}</h2>
      <div>
        {statuses.map((s, i) => (
          <Link
            key={s}
            to={`${hrefBase}?status=${s}`}
            className="between"
            style={{
              padding: 'var(--space-2) 0',
              borderBottom: i < statuses.length - 1 ? '1px solid var(--color-border)' : 'none',
              fontSize: 'var(--text-sm)',
              textDecoration: 'none',
              color: 'inherit',
              gap: 'var(--space-2)',
            }}
          >
            <StatusBadge status={s} type={type} />
            <span style={{ fontWeight: 'var(--weight-semibold)' }}>{counts[s]}</span>
          </Link>
        ))}
      </div>
    </section>
  );
}

const ATTENTION_VARIANT = {
  Overdue: 'danger',
  'Due today': 'warning',
  'Needs review': 'warning',
  'Unconfirmed event': 'neutral',
};

/** Relative "how long ago" for the activity feed — coarse on purpose. */
function timeAgo(iso) {
  if (!iso) return '';
  const ms = Date.now() - new Date(iso).getTime();
  const mins = Math.round(ms / 60000);
  if (mins < 60) return `${Math.max(mins, 0)}m ago`;
  const hours = Math.round(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.round(hours / 24);
  return `${days}d ago`;
}

export function DashboardPage() {
  usePageHeader('Dashboard', 'Your speakers, events, and engagements at a glance');
  const { user } = useAuth();
  const [engagements, setEngagements] = useState([]);
  const [events, setEvents] = useState([]);
  const [speakers, setSpeakers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    Promise.all([engagementsApi.list(), eventsApi.list(), speakersApi.list()])
      .then(([eng, evts, spks]) => {
        setEngagements(eng ?? []);
        setEvents(evts ?? []);
        setSpeakers(spks ?? []);
      })
      .catch(setError)
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <LoadingSpinner />;

  const today = todayISO();
  const eventsById = Object.fromEntries(events.map((e) => [e.id, e]));
  const speakersById = Object.fromEntries(speakers.map((s) => [s.id, s]));

  // An Event is the meeting itself (when/where). An Engagement is one
  // speaker's coordination workflow (agreement, checklist) for that event.
  const upcomingEvents = events
    .filter((e) => e.status !== 'Cancelled' && e.date >= today)
    .sort((a, b) => (a.date ?? '').localeCompare(b.date ?? ''));

  // Every speaker/talk booked for a given event, for the "Upcoming events" list.
  const engagementsByEvent = {};
  for (const e of engagements) {
    if (!e.eventId) continue;
    (engagementsByEvent[e.eventId] ??= []).push(e);
  }

  const recentSpeakers = [...speakers]
    .sort((a, b) => (b.createdAt ?? '').localeCompare(a.createdAt ?? ''))
    .slice(0, 5);
  const recentEngagements = [...engagements]
    .sort((a, b) => (b.updatedAt ?? '').localeCompare(a.updatedAt ?? ''))
    .slice(0, 5);

  const dueToday = engagements.filter(
    (e) => e.nextDueDate === today && !['Completed', 'Cancelled', 'Declined'].includes(e.status)
  );
  const overdue = engagements.filter(
    (e) =>
      e.nextDueDate &&
      e.nextDueDate < today &&
      !['Completed', 'Cancelled', 'Declined'].includes(e.status)
  );
  const needsReview = engagements.filter((e) =>
    (e.pendingSubmissions ?? []).some((s) => s.reviewStatus === 'pending')
  );
  const unconfirmedEvents = events.filter((e) => e.status === 'Draft' && e.date >= today);
  const mine = engagements.filter(
    (e) => e.ownerId === user?.sub && !['Completed', 'Cancelled', 'Declined'].includes(e.status)
  );

  // "Needs attention" — everything actually actionable right now, one combined
  // queue instead of split across separate count tiles. Sorted by urgency.
  const needsAttention = [
    ...overdue.map((e) => ({ id: `eng-od-${e.id}`, type: 'Overdue', label: e.talkTitle ?? speakersById[e.speakerId]?.firstName ?? '(untitled)', to: `/engagements/${e.id}`, date: e.nextDueDate })),
    ...dueToday.map((e) => ({ id: `eng-dt-${e.id}`, type: 'Due today', label: e.talkTitle ?? speakersById[e.speakerId]?.firstName ?? '(untitled)', to: `/engagements/${e.id}`, date: e.nextDueDate })),
    ...needsReview.map((e) => ({ id: `eng-nr-${e.id}`, type: 'Needs review', label: e.talkTitle ?? speakersById[e.speakerId]?.firstName ?? '(untitled)', to: `/engagements/${e.id}`, date: null })),
    ...unconfirmedEvents.map((e) => ({ id: `evt-uc-${e.id}`, type: 'Unconfirmed event', label: e.name, to: `/events/${e.id}/edit`, date: e.date })),
  ];

  // "Activity" — most recently touched records across every type, one feed
  // instead of three separate "recent X" lists to scan.
  const activity = [
    ...speakers.map((s) => ({ id: `spk-${s.id}`, kind: 'Speaker', label: `${s.firstName} ${s.lastName}`, to: `/speakers/${s.id}`, at: s.updatedAt ?? s.createdAt, accent: 'speaker' })),
    ...events.map((e) => ({ id: `evt-${e.id}`, kind: 'Event', label: e.name, to: `/events/${e.id}/edit`, at: e.updatedAt ?? e.createdAt, accent: 'event' })),
    ...engagements.map((e) => ({ id: `act-eng-${e.id}`, kind: 'Engagement', label: e.talkTitle ?? speakersById[e.speakerId]?.firstName ?? '(untitled)', to: `/engagements/${e.id}`, at: e.updatedAt ?? e.createdAt, accent: 'engagement' })),
  ]
    .filter((a) => a.at)
    .sort((a, b) => b.at.localeCompare(a.at))
    .slice(0, 8);

  const engByStatus = Object.fromEntries(
    ENGAGEMENT_STATUSES.map((s) => [s, engagements.filter((e) => e.status === s).length])
  );
  const spkByStatus = Object.fromEntries(
    SPEAKER_STATUSES.map((s) => [s, speakers.filter((sp) => sp.status === s).length])
  );

  const activeSpeakerCount = speakers.filter((s) => s.status === 'Active').length;
  const pipelineCount = speakers.filter((s) => ['Potential', 'Contacted', 'Interested'].includes(s.status)).length;
  const activeEngagementCount = engagements.filter(
    (e) => !['Completed', 'Cancelled', 'Declined'].includes(e.status)
  ).length;
  const nextEvent = upcomingEvents[0];

  return (
    <div>
      <div className="page-body stack">
        <ErrorAlert error={error} />

        {/* Summary — the whole pipeline at a glance, in color */}
        <div className="grid-4">
          <div className="stat-card" style={{ borderColor: 'var(--color-entity-speaker)' }}>
            <div className="stat-value" style={{ color: 'var(--color-entity-speaker)' }}>{activeSpeakerCount}</div>
            <div className="stat-label">Active speakers</div>
          </div>
          <div className="stat-card" style={{ borderColor: 'var(--color-entity-prospect)' }}>
            <div className="stat-value" style={{ color: 'var(--color-entity-prospect)' }}>{pipelineCount}</div>
            <div className="stat-label">In pipeline</div>
          </div>
          <div className="stat-card" style={{ borderColor: 'var(--color-entity-engagement)' }}>
            <div className="stat-value" style={{ color: 'var(--color-entity-engagement)' }}>{activeEngagementCount}</div>
            <div className="stat-label">Active engagements</div>
          </div>
          <div className="stat-card" style={{ borderColor: 'var(--color-entity-event)' }}>
            <div className="stat-value" style={{ color: 'var(--color-entity-event)', fontSize: nextEvent ? 'var(--text-lg)' : undefined }}>
              {nextEvent ? formatDate(nextEvent.date) : '—'}
            </div>
            <div className="stat-label">{nextEvent ? `Next: ${nextEvent.name}` : 'No upcoming events'}</div>
          </div>
        </div>

        {/* Needs attention, Activity, and the two status breakdowns — four
            25%-width queues in one row instead of stacked rows, so the page
            fits without scrolling at normal viewport heights. */}
        <div className="grid-4">
          <ListCard
            title={`Needs attention${needsAttention.length ? ` (${needsAttention.length})` : ''}`}
            items={needsAttention.slice(0, 6)}
            emptyText="Nothing needs attention."
            renderItem={(a) => ({
              primary: a.label,
              meta: <span className={`badge badge-${ATTENTION_VARIANT[a.type] ?? 'neutral'}`}>{a.type}</span>,
            })}
          />
          <ListCard
            title="Activity"
            items={activity.slice(0, 6)}
            emptyText="No recent activity."
            renderItem={(a) => ({
              primary: a.label,
              meta: timeAgo(a.at),
            })}
          />
          <StatusCard title="Engagements" statuses={ENGAGEMENT_STATUSES} counts={engByStatus} type="engagement" hrefBase="/engagements" />
          <StatusCard title="Speakers" statuses={SPEAKER_STATUSES} counts={spkByStatus} type="speaker" hrefBase="/speakers" />
        </div>

        {/* At-a-glance: the 5 most recent of each record type, each row
            clickable straight to that speaker/event/engagement. */}
        <div className="grid-3" style={{ gap: 'var(--space-4)' }}>
          <ListCard
            title="Recent speakers"
            viewAllTo="/speakers"
            accent="speaker"
            emptyText="No speakers yet."
            items={recentSpeakers.map((s) => ({ id: s.id, to: `/speakers/${s.id}`, s }))}
            renderItem={({ s }) => ({
              primary: `${s.firstName} ${s.lastName}`,
              meta: <StatusBadge status={s.status} type="speaker" />,
            })}
          />
          <ListCard
            title="Upcoming events"
            viewAllTo="/events"
            accent="event"
            emptyText="No upcoming events scheduled."
            items={upcomingEvents.slice(0, 5).map((e) => ({ id: e.id, to: `/events/${e.id}/edit`, e }))}
            renderItem={({ e }) => {
              const talks = (engagementsByEvent[e.id] ?? []);
              const first = talks[0];
              const speakerName = first ? speakersById[first.speakerId] : null;
              const topic = first?.talkTitle;
              const detail = speakerName
                ? `${speakerName.firstName} ${speakerName.lastName}${topic ? ` — ${topic}` : ''}${talks.length > 1 ? ` +${talks.length - 1} more` : ''}`
                : formatDate(e.date);
              return { primary: e.name, meta: detail };
            }}
          />
          <ListCard
            title="Recent engagements"
            viewAllTo="/engagements"
            accent="engagement"
            emptyText="No engagements yet."
            items={recentEngagements.map((eng) => ({ id: eng.id, to: `/engagements/${eng.id}`, eng }))}
            renderItem={({ eng }) => {
              const evt = eventsById[eng.eventId];
              return {
                primary: eng.talkTitle ?? evt?.name ?? '(untitled)',
                meta: <StatusBadge status={eng.status} />,
              };
            }}
          />
        </div>

        {/* My records */}
        {mine.length > 0 && (
          <section>
            <h2 style={{ fontSize: 'var(--text-lg)', marginBottom: 'var(--space-3)' }}>My assignments</h2>
            <div className="data-table" style={{ overflowX: 'auto' }}>
              <table>
                <thead>
                  <tr><th>Talk</th><th>Status</th><th>Next due</th></tr>
                </thead>
                <tbody>
                  {mine.slice(0, 8).map((e) => (
                    <tr key={e.id}>
                      <td><Link to={`/engagements/${e.id}`}>{e.talkTitle ?? '(untitled)'}</Link></td>
                      <td><StatusBadge status={e.status} /></td>
                      <td>{formatDate(e.nextDueDate)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>
        )}
      </div>
    </div>
  );
}
